
import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('interest', formData.interest);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4iik6ijg2jl50j1sdk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', interest: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24 lg:py-32 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-10 sm:top-20 right-0 w-48 sm:w-96 h-48 sm:h-96 bg-blue-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-10 sm:bottom-20 left-0 w-48 sm:w-96 h-48 sm:h-96 bg-red-500/5 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left Column - Info */}
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 mb-4 sm:mb-6 bg-blue-500/10 border border-blue-500/20 px-3 sm:px-5 py-2 rounded-full">
              <div className="w-2 h-2 bg-blue-600 rounded-full" />
              <span className="text-blue-700 font-semibold text-xs sm:text-sm tracking-wider uppercase">Get In Touch</span>
            </div>
            
            <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 sm:mb-8 leading-tight">
              Let's Discuss Your{' '}
              <span className="bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
                Real Estate Goals
              </span>
            </h3>
            
            <p className="text-base sm:text-lg text-gray-600 mb-8 sm:mb-12 leading-relaxed">
              Whether you're looking to buy, sell, invest, or build, our team of experts is ready to guide you through every step of your real estate journey. Contact us today for a consultation.
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-4 sm:space-y-6 mb-8 sm:mb-12">
              <div className="group flex items-start gap-3 sm:gap-5 p-4 sm:p-6 rounded-2xl bg-white border border-gray-100 hover:border-blue-500/30 hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="w-12 sm:w-14 h-12 sm:h-14 flex items-center justify-center bg-gradient-to-br from-red-500 to-blue-600 rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-map-pin-line text-xl sm:text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1 sm:mb-2 text-base sm:text-lg">Visit Our Office</h4>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">123 Business Avenue, Suite 500<br />Metropolitan City, MC 12345</p>
                </div>
              </div>

              <div className="group flex items-start gap-3 sm:gap-5 p-4 sm:p-6 rounded-2xl bg-white border border-gray-100 hover:border-blue-500/30 hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="w-12 sm:w-14 h-12 sm:h-14 flex items-center justify-center bg-gradient-to-br from-red-500 to-blue-600 rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-phone-line text-xl sm:text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1 sm:mb-2 text-base sm:text-lg">Call Us</h4>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">+1 (555) 123-4567<br />Mon - Sat: 9:00 AM - 6:00 PM</p>
                </div>
              </div>

              <div className="group flex items-start gap-3 sm:gap-5 p-4 sm:p-6 rounded-2xl bg-white border border-gray-100 hover:border-blue-500/30 hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="w-12 sm:w-14 h-12 sm:h-14 flex items-center justify-center bg-gradient-to-br from-red-500 to-blue-600 rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-mail-line text-xl sm:text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1 sm:mb-2 text-base sm:text-lg">Email Us</h4>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">info@madinaassociates.com<br />support@madinaassociates.com</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 sm:gap-4">
              <a href="#" className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gradient-to-br hover:from-red-500 hover:to-blue-600 text-gray-600 hover:text-white hover:border-transparent transition-all hover:scale-110 cursor-pointer shadow-sm">
                <i className="ri-facebook-fill text-lg sm:text-xl"></i>
              </a>
              <a href="#" className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gradient-to-br hover:from-red-500 hover:to-blue-600 text-gray-600 hover:text-white hover:border-transparent transition-all hover:scale-110 cursor-pointer shadow-sm">
                <i className="ri-twitter-fill text-lg sm:text-xl"></i>
              </a>
              <a href="#" className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gradient-to-br hover:from-red-500 hover:to-blue-600 text-gray-600 hover:text-white hover:border-transparent transition-all hover:scale-110 cursor-pointer shadow-sm">
                <i className="ri-linkedin-fill text-lg sm:text-xl"></i>
              </a>
              <a href="#" className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gradient-to-br hover:from-red-500 hover:to-blue-600 text-gray-600 hover:text-white hover:border-transparent transition-all hover:scale-110 cursor-pointer shadow-sm">
                <i className="ri-instagram-fill text-lg sm:text-xl"></i>
              </a>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="relative order-1 lg:order-2">
            {/* Decorative Corner */}
            <div className="absolute -top-4 sm:-top-6 -right-4 sm:-right-6 w-16 sm:w-24 h-16 sm:h-24 bg-gradient-to-br from-blue-500/20 to-transparent rounded-3xl -z-10" />
            
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-gray-100">
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                <div className="mb-4 sm:mb-6">
                  <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2 sm:mb-3">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 sm:px-5 py-3 sm:py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-sm bg-gray-50 focus:bg-white"
                    placeholder="John Doe"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2 sm:mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-sm bg-gray-50 focus:bg-white"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-gray-900 mb-2 sm:mb-3">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-sm bg-gray-50 focus:bg-white"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                <div className="mb-4 sm:mb-6">
                  <label htmlFor="interest" className="block text-sm font-bold text-gray-900 mb-2 sm:mb-3">
                    I'm Interested In *
                  </label>
                  <div className="relative">
                    <select
                      id="interest"
                      name="interest"
                      value={formData.interest}
                      onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 pr-10 sm:pr-12 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all appearance-none cursor-pointer text-sm bg-gray-50 focus:bg-white"
                    >
                      <option value="">Select an option</option>
                      <option value="buying">Buying Property</option>
                      <option value="selling">Selling Property</option>
                      <option value="construction">Construction Services</option>
                      <option value="investment">Investment Consulting</option>
                      <option value="land">Land Development</option>
                      <option value="other">Other Services</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-lg sm:text-xl"></i>
                  </div>
                </div>

                <div className="mb-6 sm:mb-8">
                  <label htmlFor="message" className="block text-sm font-bold text-gray-900 mb-2 sm:mb-3">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 sm:px-5 py-3 sm:py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all resize-none text-sm bg-gray-50 focus:bg-white min-h-[100px] sm:min-h-[120px]"
                    placeholder="Tell us about your requirements..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-2 text-right font-medium">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-600 to-blue-600 text-white px-6 sm:px-8 py-4 sm:py-5 rounded-xl hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 font-bold text-base sm:text-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Send Message
                      <i className="ri-send-plane-fill"></i>
                    </span>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="mt-4 sm:mt-6 p-4 sm:p-5 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 rounded-xl text-green-800 text-sm flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <strong className="font-bold block mb-1">Success!</strong>
                      Your message has been sent successfully. We'll get back to you soon.
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-4 sm:mt-6 p-4 sm:p-5 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl text-red-800 text-sm flex items-start gap-3">
                    <i className="ri-error-warning-fill text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <strong className="font-bold block mb-1">Error!</strong>
                      Sorry, there was an error sending your message. Please try again.
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
